import React, { useState, useEffect } from "react"; // Removed useRef
import { Container, Button, ListGroup, Row, Col, Card, Form, InputGroup, Alert } from "react-bootstrap"; // Added Form, InputGroup, Alert
// Remove GooglePayButton import
// import GooglePayButton from "@google-pay/button-react";
import { cartData } from "../functions/api/cartData";
import { useReport } from "../context/ReportModal";
import { useProcessing } from "../context/ProcessingModal";
// Update icons if needed, FaCreditCard might be reused for Account Credit
import { FaShoppingCart, FaCreditCard, FaUniversity, FaFileUpload } from "react-icons/fa"; // Added FaFileUpload
import { Link } from "react-router-dom";
import { runData } from "../functions/api/processor";
import { Helmet } from "react-helmet";
import PageHeader from "../components/PageHeader";
import PaymentInstructionsModal from "../components/PaymentInstructionsModal"; // Import the new instructions modal
import UploadPopModal from "../components/UploadPopModal"; // Import the renamed upload modal

const CheckoutPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const { setReport } = useReport();
  const { setProcessing } = useProcessing();
  const [seoData, setSeoData] = useState({
    title: "Loading...",
    meta_description: "",
    keywords: "",
    og_title: "",
    og_description: "",
    og_image: "", // fallback image
    canonical_url: window.location.href,
  });
  const [accountBalance, setAccountBalance] = useState(0); // Add state for account balance
  const [showInstructionsModal, setShowInstructionsModal] = useState(false); // State for instructions modal
  const [showUploadPopModal, setShowUploadPopModal] = useState(false); // State for upload modal

  // Fetch cart items on mount
  useEffect(() => {
    const fetchCart = async () => {
      try {
        const items = await cartData("get");
        setCartItems(items);
        // Calculate total by summing the 'calculatedTotal' field from each cart item
        const total = items.reduce(
          (acc, item) => acc + Number(item.calculatedTotal || 0), // Use calculatedTotal
          0
        );
        setTotalAmount(total);
      } catch (err) {
        setReport({
          show: true,
          message: err.message || "Failed to load cart items.",
          type: "error",
        });
      }
    };
    fetchCart();
  }, [setReport]);

  useEffect(() => {
    (async () => {
      try {
        // Use runData with 'GET' method for SEO data
        const response = await runData({ page: "checkout" }, "/web/pages/", 'GET');
        if (response.status === 200 && response.data.seo) {
          let seoResponse = response.data.seo;
          if (
            seoResponse.canonical_url &&
            seoResponse.canonical_url.startsWith("/")
          ) {
            let hostname = window.location.hostname;
            if (!hostname.startsWith("www.")) {
              hostname = "www." + hostname;
            }
            seoResponse.canonical_url =
              window.location.protocol + "//" + hostname + seoResponse.canonical_url;
          }
          setSeoData(seoResponse);
        }
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  // Updated function to remove an item from the cart (by "deleting" the order - setting status 0)
  const handleRemoveItem = async (index, item) => {
    const itemReference = item.reference;

    if (!itemReference) {
        // ... existing error handling ...
        return;
    }

    setProcessing(true);

    try {
        // 1. Call backend to "delete" the order (set status to 0)
        // Use POST method and the new delete endpoint
        const deleteEndpoint = `/api/orders/${itemReference}/delete`; // Updated endpoint
        const response = await runData({}, deleteEndpoint); // Method defaults to POST

        // 2. Check if backend update was successful (200 OK or 204 No Content)
        if (response.status === 200 || response.status === 204) {
            // 3. If successful, remove locally from cartData and state
            await cartData("remove", itemReference);
            const newCart = cartItems.filter((cartItem) => cartItem.reference !== itemReference);
            setCartItems(newCart);

            // Recalculate total
            const newTotal = newCart.reduce(
                (acc, curr) => acc + Number(curr.calculatedTotal || 0),
                0
            );
            setTotalAmount(newTotal);

            setReport({
                show: true,
                message: "Order removed successfully.", // User-friendly message
                type: "success",
            });
        } else {
            // Handle backend update failure
            const errorMsg = response.data?.message || "Failed to remove order on server.";
            setReport({ show: true, message: errorMsg, type: "error" });
        }
    } catch (err) {
        // Handle network or other errors
        setReport({
            show: true,
            message: err.message || "Failed to remove order due to a network or server error.",
            type: "error",
        });
    } finally {
        setProcessing(false);
    }
  };

  // Placeholder function for handling payment with account credit
  const handlePayWithCredit = async () => {
    if (totalAmount > accountBalance) {
      setReport({ show: true, message: "Insufficient account balance.", type: "error" });
      return;
    }
    if (cartItems.length === 0) {
       setReport({ show: true, message: "Your cart is empty.", type: "warning" });
       return;
    }

    setProcessing(true);
    // TODO: Implement API call to backend to process payment using credit
    // Pass cartItems references and totalAmount
    console.log("Processing payment with account credit for total:", totalAmount);
    // Example: const response = await runData({ items: cartItems.map(i => i.reference), total: totalAmount }, '/api/pay/credit', 'POST');
    // Handle response, clear cart, show success/error
    await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call
    setProcessing(false);
    setReport({ show: true, message: "Payment with account credit successful (simulation).", type: "success" });
    // Clear cart locally after successful payment
    await cartData("clear");
    setCartItems([]);
    setTotalAmount(0);
  };

  // Updated function for handling manual payment confirmation + POP upload
  // Now accepts the file from the modal
  const handleConfirmManualPayment = async (uploadedFile) => {
     if (!uploadedFile) {
       // This check might be redundant if modal enforces file selection, but good practice
       setReport({ show: true, message: "Proof of Payment file is missing.", type: "warning" });
       return;
     }
     if (cartItems.length === 0) {
       setReport({ show: true, message: "Your cart is empty.", type: "warning" });
       return;
     }

     setShowUploadPopModal(false); // Close upload modal before processing
     setProcessing(true);

     try {
        // 1. Create FormData
        const formData = new FormData();
        formData.append('pop', uploadedFile); // Use the file passed from the modal
        formData.append('items', JSON.stringify(cartItems.map(i => i.reference)));
        // TODO: Ensure runData can handle FormData correctly or use fetch directly
        // const response = await runData(formData, '/api/pay/manual');
        console.log("Confirming manual payment for total:", totalAmount, "with POP:", uploadedFile.name);
        await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call

        // Handle response...
        setReport({ show: true, message: "Manual payment confirmation and POP submitted (simulation). Awaiting verification.", type: "success" });
        // Clear cart locally after successful submission
        await cartData("clear");
        setCartItems([]);
        setTotalAmount(0);
     } catch (err) {
        setReport({ show: true, message: err.message || "Failed to submit manual payment confirmation.", type: "error" });
     } finally {
        setProcessing(false);
     }
  };

  // Function to handle closing instructions and opening upload modal
  const handleInstructionsConfirm = () => {
    setShowInstructionsModal(false);
    setShowUploadPopModal(true);
  };

  // Google Pay paymentRequest configuration.
  const paymentRequest = {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [
      {
        type: "CARD",
        parameters: {
          allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
          allowedCardNetworks: ["MASTERCARD", "VISA"],
        },
        // Changed: Use direct integration since we are not using a payment provider.
        tokenizationSpecification: {
          type: "DIRECT",
          parameters: {
            protocolVersion: "ECv2",
            publicKey: "YOUR_PUBLIC_KEY_HERE", // Replace with your actual public key
          },
        },
      },
    ],
    merchantInfo: {
      merchantId: "BCR2DN4T27RZVLL6", // Replace with your merchant ID
      merchantName: "Marmasco Technologies",
    },
    transactionInfo: {
      totalPriceStatus: "FINAL",
      totalPriceLabel: "Total",
      totalPrice: totalAmount.toFixed(2),
      currencyCode: "USD",
      countryCode: "US",
    },
  };

  // Called after a successful Google Pay payment.
  const handlePaymentSuccess = (paymentData) => {
    // Here, send paymentData to your server for verification and order processing.
    setReport({
      show: true,
      message: "Payment successful via Google Pay. Processing order...",
      type: "success",
    });
    // For demo purposes, we simply clear the cart or navigate.
  };

  const handlePaymentError = (error) => {
    setReport({
      show: true,
      message: error.message || "Payment failed via Google Pay.",
      type: "error",
    });
  };

  return (
    <>
      <Helmet>
        {/* Use seoData for Helmet */}
        <title>{seoData.title} - Marmasco Technologies</title>
        <meta name="description" content={seoData.meta_description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta property="og:title" content={seoData.og_title} />
        <meta property="og:description" content={seoData.og_description} />
        <meta property="og:image" content={seoData.og_image} />
        <link rel="canonical" href={seoData.canonical_url} />
        {/* Add other relevant meta tags */}
      </Helmet>

      {/* Use the reusable PageHeader component */}
      <PageHeader seoData={seoData} />

      {/* Removed the custom header Container and div */}

      {/* Main Content Section */}
      <Container
        id="content-start" // Added id for potential linking
        className="bg-light py-5 fade-in mb-4 rounded-4 shadow-lg" // Container has bg-light
        style={{ marginTop: "-6rem", borderRadius: "1rem", position: "relative", zIndex: 1 }} // Updated styles to overlap header
      >
        {/* Changed Header for the content area */}
        <Row className="mb-5">
          <Col className="text-center px-md-4 px-2">
            {/* Changed the heading text below */}
            <h2 className="display-5 fw-bold mb-3 section-title">Order Review & Payment</h2>
            <p className="lead text-muted mx-auto" style={{ maxWidth: "800px" }}>
              Review your order and choose your preferred payment method below.
            </p>
          </Col>
        </Row>

        {cartItems.length > 0 ? (
          <Row className="px-md-4 px-2">
            <Col xs={12}>
              {/* Apply consistent card styling */}
              <Card className="mb-4 shadow-sm border-0 rounded-3"> {/* Added rounded-3 */}
                <Card.Header as="h5">Your Order Summary</Card.Header>
                <ListGroup variant="flush">
                  {cartItems.map((item, idx) => {
                    let itemTitle = "";
                    let itemDetails = "";
                    // Use the pre-calculated calculatedTotal for display
                    const value = Number(item.calculatedTotal || 0); // Use calculatedTotal
                    // Parse offer string if needed for details (optional)
                    const offerData = item.offer && typeof item.offer === 'string' ? JSON.parse(item.offer) : item.offer;

                    switch (item.product) {
                      case "hosting": // Renamed from suite
                        itemTitle = "Shared Hosting Order"; // Updated title
                        // Use details from the main item object
                        itemDetails = `Domain: ${item.domainName || "N/A"}, Users: ${item.numberOfUsers || "N/A"}, Cycle: ${item.billingCycle || "N/A"}`;
                        break;
                      case "servers": // Renamed from hosting
                        itemTitle = "Server Order";
                        // Use details from the main item object
                        itemDetails = `Server: ${item.serverType || "N/A"}, Hostname: ${item.name || "N/A"}, Location: ${item.location || "N/A"}, Cycle: ${item.cycle || "N/A"}`;
                        break;
                      case "domain":
                        itemTitle = "Domain Order";
                        // Use details from the main item object
                        itemDetails = `Domain: ${item.domain || "N/A"}, Action: ${item.action || "N/A"}`;
                        break;
                      default:
                        itemTitle = item.product || "Unknown Item";
                        break;
                    }
                    return (
                      <ListGroup.Item key={item.reference || idx} className="px-3 py-3"> {/* Use reference as key if available */}
                        <Row className="align-items-center">
                          <Col xs="auto">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="py-1 px-2"
                              onClick={() => handleRemoveItem(idx, item)}
                              aria-label="Remove item"
                            >
                              &times;
                            </Button>
                          </Col>
                          <Col>
                            <strong>{itemTitle}</strong>
                            <div className="text-muted small">{itemDetails}</div>
                          </Col>
                          <Col xs="auto" className="text-end fw-bold">
                            {/* Display the pre-calculated itemTotal */}
                            ${value.toFixed(2)}
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    );
                  })}
                  {/* Total Row */}
                  <ListGroup.Item className="px-3 py-3 bg-light"> {/* Keep bg-light here for visual separation of total */}
                    <Row>
                      <Col>
                        <strong className="fs-5">Total</strong>
                      </Col>
                      <Col className="text-end">
                        {/* Display the totalAmount state */}
                        <strong className="fs-5">${totalAmount.toFixed(2)}</strong>
                      </Col>
                    </Row>
                  </ListGroup.Item>
                </ListGroup>
              </Card>
            </Col>
            <Col xs={12} className="mt-4"> {/* Add margin top */}
              <h4 className="mb-4 section-title text-center">Select Payment Method</h4>
            </Col>

            {/* Payment Options */}
            {/* Option 1: Account Credit */}
            <Col md={6} className="mb-4 mb-md-0 d-flex align-items-stretch">
              <Card className="h-100 border-0 shadow-sm rounded-3 w-100">
                <Card.Body className="text-center d-flex flex-column p-4">
                  <h5 className="mb-3"><FaCreditCard className="me-2 text-primary" />Pay with Account Credit</h5>
                  <div className="flex-grow-1 mb-3"> {/* Allow text to take space */}
                    <p className="lead">Available Balance: <strong>${accountBalance.toFixed(2)}</strong></p>
                    {totalAmount > accountBalance && (
                      <Alert variant="warning" size="sm" className="py-1 px-2 small">
                        Insufficient balance. Add funds or choose another method.
                      </Alert>
                    )}
                  </div>
                  <Button
                    variant="success" // Changed variant
                    onClick={handlePayWithCredit}
                    disabled={totalAmount > accountBalance || totalAmount === 0} // Disable if insufficient balance or cart empty
                    className="w-100 mt-auto" // Push button to bottom
                  >
                    Use Credit for ${totalAmount.toFixed(2)}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Option 2: Send Money Manually */}
            <Col md={6} className="d-flex align-items-stretch">
              <Card className="h-100 border-0 shadow-sm rounded-3 w-100">
                <Card.Body className="text-center d-flex flex-column p-4">
                  <h5 className="mb-3"><FaUniversity className="me-2 text-primary" />Send Money Manually</h5>
                  <p className="text-muted flex-grow-1">
                    Complete your payment using external platforms (EcoCash, InnBucks, etc.) or Bank Transfer, then upload your Proof of Payment (POP).
                  </p>
                  {/* Update button onClick to show the instructions modal */}
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowInstructionsModal(true)} // Show instructions modal
                    className="w-100 mt-auto" // Use mt-auto to push to bottom
                  >
                    Payment Instructions
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : (
          <Row className="px-md-4 px-2">
            <Col className="text-center">
              <p className="lead">Your cart is currently empty.</p>
              <Button as={Link} to="/" variant="primary">Continue Shopping</Button>
            </Col>
          </Row>
        )}
      </Container>

      {/* Add the PaymentInstructionsModal component */}
      <PaymentInstructionsModal
        show={showInstructionsModal}
        onHide={() => setShowInstructionsModal(false)}
        onConfirm={handleInstructionsConfirm} // Link confirmation to opening the upload modal
        totalAmount={totalAmount}
      />

      {/* Add the UploadPopModal component */}
      <UploadPopModal
        show={showUploadPopModal}
        onHide={() => setShowUploadPopModal(false)}
        onSubmit={handleConfirmManualPayment} // Pass the existing handler
      />
    </>
  );
};

export default CheckoutPage;
